// CompanyScroller.js
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Box, Container } from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Lnt from '../images/LT.png';
import tatamotor from '../images/TATA-Motors.jpg';
import tcs from '../images/TCS.NS.png';
import adani from '../images/adani.png';
import HINDALCO from '../images/hindalco_logo.png';
import icici from '../images/icici-bank.png';
import india from '../images/india.png';
import reliance from '../images/reliance.png';
import sbi from '../images/sbi-logo.png';
import tatapower from '../images/tata-power-logo.png';
import getStockData from '../stockService';

const ACCENT = '#ef6a1d';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';
const UP = '#22c55e';
const DOWN = '#ef4444';

const scroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
  50%      { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
`;

const Marquee = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 45s linear infinite;
  will-change: transform;
`;

const MarqueeViewport = styled.div`
  position: relative;
  overflow: hidden;
  padding: 18px 0;
  /* Edge fade masks */
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 60px,
    #000 calc(100% - 60px),
    transparent 100%
  );
  mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 60px,
    #000 calc(100% - 60px),
    transparent 100%
  );

  &:hover ${Marquee} {
    animation-play-state: paused;
  }
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${UP};
  display: inline-block;
  animation: ${pulse} 1.6s ease-out infinite;
`;

const companies = [
  { name: 'TCS', symbol: 'TCS', link: 'https://finance.stockdio.com/profile/INDIA_NSE/TCS?s=1', logo: tcs },
  { name: 'Adani', symbol: 'ADANIPOWER.BSE', link: 'https://finance.stockdio.com/profile/INDIA_NSE/ADANI?s=1', logo: adani },
  { name: 'Tata Motors', symbol: 'TTM', link: 'https://finance.stockdio.com/profile/INDIA_NSE/TATAMOTORS?s=1', logo: tatamotor },
  { name: 'Reliance', symbol: 'Reliance', link: 'https://finance.stockdio.com/profile/INDIA_NSE/RELIANCE?s=1', logo: reliance },
  { name: 'SBIN', symbol: 'SBIN', link: 'https://finance.stockdio.com/profile/INDIA_NSE/SBIN?s=1', logo: sbi },
  { name: 'L&T', symbol: 'LT', link: 'https://finance.stockdio.com/profile/INDIA_NSE/LT?s=1', logo: Lnt },
  { name: 'CNX NIFTY', symbol: 'CNXNIFTY', link: 'https://finance.stockdio.com/profile/INDIA_NSE/%5ECNXNIFTY?s=1', logo: india },
  { name: 'HINDALCO', symbol: 'HINDALCO', link: 'https://finance.stockdio.com/profile/INDIA_NSE/HINDALCO?s=1', logo: HINDALCO },
  { name: 'ICICI Bank', symbol: 'ICICIBANK', link: 'https://finance.stockdio.com/profile/INDIA_NSE/ICICIBANK?s=1', logo: icici },
  { name: 'INR / USD', symbol: 'INR/USD', link: 'https://finance.stockdio.com/profile/FOREX/INR/USD?s=1', logo: india },
  { name: 'Tata Power', symbol: 'TATAPOWER', link: 'https://finance.stockdio.com/profile/INDIA_NSE/TATAPOWER?s=1', logo: tatapower },
];

const StockCard = ({ company, info }) => {
  const price = info ? parseFloat(info['05. price']) : null;
  const change = info ? parseFloat(info['09. change']) : null;
  const changePct = info ? parseFloat(info['10. change percent']) : null;
  const positive = change != null && change >= 0;

  return (
    <Box
      onClick={() => window.open(company.link, '_blank')}
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        mx: 1,
        minWidth: 220,
        cursor: 'pointer',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(6px)',
        transition: 'all 0.25s ease',
        '&:hover': {
          background: 'rgba(255,255,255,0.1)',
          borderColor: 'rgba(239,106,29,0.4)',
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* Logo tile */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={company.logo}
          alt={company.name}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </Box>

      {/* Name + price */}
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: 0.3,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          {company.name}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2 }}>
          <Box
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#fff',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {price != null ? price.toFixed(2) : '—'}
          </Box>
          {change != null ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 0.6,
                borderRadius: 1,
                fontSize: '0.78rem',
                fontWeight: 700,
                color: positive ? UP : DOWN,
                background: positive
                  ? 'rgba(34,197,94,0.12)'
                  : 'rgba(239,68,68,0.12)',
                fontVariantNumeric: 'tabular-nums',
                '& svg': { fontSize: '1rem', ml: -0.4 },
              }}
            >
              {positive ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              {changePct != null
                ? `${Math.abs(changePct).toFixed(2)}%`
                : Math.abs(change).toFixed(2)}
            </Box>
          ) : (
            <Box
              sx={{
                fontSize: '0.72rem',
                color: '#94a3b8',
                fontStyle: 'italic',
              }}
            >
              loading
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CompanyScroller = () => {
  const [stockData, setStockData] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchStockData = async () => {
      const data = {};
      for (const company of companies) {
        try {
          const stockInfo = await getStockData(company.symbol);
          if (stockInfo) data[company.symbol] = stockInfo;
        } catch {
          /* ignore — rate limits etc. */
        }
        if (cancelled) return;
      }
      if (!cancelled) setStockData(data);
    };

    fetchStockData();
    const intervalId = setInterval(fetchStockData, 60000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Render the list twice for a seamless loop
  const track = [...companies, ...companies];

  return (
    <Box
      sx={{
        position: 'relative',
        background: `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)`,
        color: '#fff',
        py: { xs: 3.5, md: 4.5 },
        borderTop: `1px solid rgba(255,255,255,0.06)`,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 50%, transparent 100%)`,
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                background: 'rgba(239,106,29,0.15)',
                color: ACCENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(239,106,29,0.3)',
              }}
            >
              <ShowChartIcon />
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: 1.4,
                  color: ACCENT,
                  textTransform: 'uppercase',
                }}
              >
                Live Market
              </Box>
              <Box
                sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}
              >
                Track the markets in real time
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.4,
              py: 0.5,
              borderRadius: 5,
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              color: UP,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: 0.6,
            }}
          >
            <LiveDot />
            LIVE
          </Box>
        </Box>
      </Container>

      <MarqueeViewport>
        <Marquee>
          {track.map((company, index) => (
            <StockCard
              key={`${company.symbol}-${index}`}
              company={company}
              info={stockData[company.symbol]}
            />
          ))}
        </Marquee>
      </MarqueeViewport>
    </Box>
  );
};

export default CompanyScroller;
